import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import SomethingWentWrong from 'src/components/SomethingWentWrong';
import PageLoading from 'src/components/PageLoading';
import { StyledPanel } from 'src/Esg/styles';
import NoData from 'src/components/NoData';
import { useEsgReports } from 'src/Esg/hooks';
import {
  useEsgStakeholderData,
  useEsgStakeholderSurveys,
  useStakeholderTopics,
} from 'src/Esg/Dmav2/hooks';
import { Divider, IconButton, Typography, Box } from '@mui/material';
import { StyledFlex } from 'src/components/StyledFlex';
import PaginatorSwitch, {
  usePaginationSwitch,
} from 'src/components/PaginatorSwitch';
import { Delete } from '@mui/icons-material';
import QuestionnaireTable from 'src/Esg/Dmav2/StakeholderSurvey/Questionnaire/Table';
import ConfirmationModal from 'src/components/ConfirmationModal';
import { getSurveyNumber } from 'src/Esg/Dmav2/StakeholderSurvey/utils';
import CustomMuiSelect from 'src/components/CustomMuiSelect';
import { useTranslation } from 'react-i18next';

export default function Individual() {
  const { reportId } = useParams();
  const { _instance: report } = useEsgReports({ id: Number(reportId) });
  const {
    _data: surveys,
    isLoading,
    isError,
    _delete,
  } = useEsgStakeholderSurveys({ esg_dma_id: report?.esg_dma_id });

  const { _data: stakeholderDataEntries } = useEsgStakeholderData({
    esg_dma_id: report?.esg_dma_id,
  });

  const { t } = useTranslation(undefined, {
    keyPrefix: 'esg:dma.stakeholderAnalytics.individual',
  });

  const [selectedStakeholderTypeId, setSelectedStakeholderTypeId] = useState<
    number | undefined
  >(undefined);

  const filteredSurveys = useMemo(() => {
    if (selectedStakeholderTypeId === undefined) {
      return surveys;
    }
    return surveys.filter(
      (survey) => survey.esg_stakeholder_type_id === selectedStakeholderTypeId
    );
  }, [surveys, selectedStakeholderTypeId]);

  const { currPage, setCurrPage, handlePrevClick, handleNextClick } =
    usePaginationSwitch();

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const handleDelete = () =>
    _delete.mutateAsync(filteredSurveys[currPage].id).then(() => {
      setCurrPage(0);
      setDeleteConfirmationOpen(false);
    });

  const stakeholderData = stakeholderDataEntries?.find(
    (entry) =>
      entry.esg_stakeholder_type_id ===
      filteredSurveys[currPage]?.esg_stakeholder_type_id
  );

  const { subTopics, subSubTopics } = useStakeholderTopics(stakeholderData?.id);

  const survey = filteredSurveys[currPage];

  const stakeholderOptions = useMemo(() => {
    if (!surveys) return [];
    const uniqueTypes = surveys.reduce(
      (acc, survey) => {
        if (!acc.some((type) => type.id === survey.esg_stakeholder_type_id)) {
          acc.push({
            id: survey.esg_stakeholder_type_id,
            name: survey.esg_stakeholder_type_name,
          });
        }
        return acc;
      },
      [] as { id: number; name: string | null }[]
    );

    return uniqueTypes.map((type) => ({
      label: type.name || `Type ${type.id}`,
      value: type.id.toString(),
    }));
  }, [surveys]);

  const handleStakeholderChange = (value: string) => {
    setCurrPage(0);
    if (value === '') {
      setSelectedStakeholderTypeId(undefined);
    } else {
      setSelectedStakeholderTypeId(Number(value));
    }
  };

  useEffect(() => {
    if (!survey && filteredSurveys.length > 0) {
      setCurrPage(0);
    }
  }, [survey, filteredSurveys.length]);

  if (isLoading) return <PageLoading />;
  if (isError) return <SomethingWentWrong />;
  if (!surveys || surveys.length === 0) return <NoData />;

  if (!survey) {
    return <PageLoading />;
  }
  if (!survey) return <NoData />;

  return (
    <StyledPanel sx={{ minWidth: '940px' }}>
      {stakeholderOptions.length > 0 && (
        <Box sx={{ mb: 2, width: '300px' }}>
          <CustomMuiSelect
            label=""
            options={[
              { label: t('allStakeholdersTypes'), value: '' },
              ...stakeholderOptions,
            ]}
            value={
              selectedStakeholderTypeId ? String(selectedStakeholderTypeId) : ''
            }
            onChange={handleStakeholderChange}
          />
          <Divider sx={{ m: '20px -32px 0px -32px' }} />
        </Box>
      )}

      <StyledFlex sx={{ mt: '-12px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <PaginatorSwitch
            currPage={currPage}
            pagesCount={filteredSurveys.length}
            onPrevClick={handlePrevClick}
            onNextClick={handleNextClick}
          />
          <Typography variant="h3">
            {survey.esg_stakeholder_type_name} #{currPage + 1} /{' '}
            {filteredSurveys.length}
            {selectedStakeholderTypeId !== undefined
              ? ` (${t('filtered')})`
              : ''}
          </Typography>
        </div>
        <IconButton onClick={() => setDeleteConfirmationOpen(true)}>
          <Delete />
        </IconButton>
      </StyledFlex>

      <Divider sx={{ m: '20px -32px' }} />
      <QuestionnaireTable
        readOnly
        esgStakeholderSurveyId={survey.id}
        topics={{ sub_topics: subTopics, sub_sub_topics: subSubTopics }}
      />
      <Divider sx={{ m: '20px -32px' }} />

      <Typography
        sx={{
          border: '1px solid #ccc',
          borderRadius: '8px',
          p: '8px',
          backgroundColor: '#f9f9f9',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
        }}
      >
        {survey.quality_answer || '-'}
      </Typography>
      <ConfirmationModal
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        onFalse={() => setDeleteConfirmationOpen(false)}
        textKey="esg:dma.stakeholderAnalytics.tables.deleteConfirmationText"
        titleKey="esg:dma.stakeholderAnalytics.tables.deleteConfirmationTitle"
        onTrue={handleDelete}
      />
    </StyledPanel>
  );
}
